import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const RoleManagementPanel = withListOverflow(RoleManagementListOwner);
// src/features/admin/RoleManagementPanel.jsx
//
// ADMIN-OKTA-PERMISSIONS-UI: Okta group → app role mapping editor.
//
// Layout (top to bottom):
//   1. Panel heading + description
//   2. Editable mapping table (okta_group input + app_role select + delete)
//      "+ Add mapping" button appends an empty row
//      "Save changes" calls PUT /api/admin/role-mappings

import React, { useEffect, useState, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  getRoleMappings,
  putRoleMappings,
  getSensitivityTaxonomy,
  putSensitivityTaxonomy,
  AuthError,
  ValidationError,
} from "../../data/adminUsersClient.js";
import { log } from "../../utils/logger.js";

// ─── Style constants ──────────────────────────────────────────────────────────

const TITLE = {
  margin: 0,
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  letterSpacing: -0.2,
};

const SUBTITLE = {
  fontSize: T.fsBase,
  color: T.mid,
  marginBottom: 22,
  marginTop: 5,
  lineHeight: 1.5,
};

const TH = {
  padding: "8px 12px",
  fontSize: T.fsXs,
  fontWeight: T.fwBold,
  textTransform: "uppercase",
  letterSpacing: 0.06,
  color: T.mid,
  background: T.lightGray,
  borderBottom: `1px solid ${T.border}`,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const TD = {
  padding: "9px 12px",
  fontSize: T.fsSm5,
  borderBottom: `0.5px solid ${T.border}`,
  verticalAlign: "middle",
};

const ROLE_CAPABILITIES = [
  { role: "admin", title: "Admin", summary: "Full administrative controls, including access governance, policy settings, and portfolio configuration." },
  { role: "editor", title: "Editor", summary: "Portfolio editing and operational workflows within the existing editor boundaries." },
  { role: "viewer", title: "Viewer", summary: "Full read-only access to portfolio information, including financial data. Cannot edit." },
];

function launchRole(role) {
  return role === "view_limited" ? "viewer" : role;
}

// ─── Main panel ───────────────────────────────────────────────────────────────

function RoleManagementListOwner({ explainer } = {}) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [dirty,   setDirty]   = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);

  // Load current mappings on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRoleMappings()
      .then(data => {
        if (cancelled) return;
        setRows((data.mappings ?? []).map((m, i) => ({
          _key:      i,
          okta_group: m.okta_group,
          app_role:   launchRole(m.app_role),
        })));
      })
      .catch(err => {
        if (cancelled) return;
        if (err instanceof AuthError) {
          setError("Permission denied.");
        } else {
          setError("Failed to load role mappings.");
          log.error("RoleManagementPanel: getRoleMappings failed", { err: String(err) });
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleGroupChange = useCallback((key, val) => {
    setRows(prev => prev.map(r => r._key === key ? { ...r, okta_group: val } : r));
    setDirty(true);
    setSuccess(false);
  }, []);

  const handleRoleChange = useCallback((key, val) => {
    setRows(prev => prev.map(r => r._key === key ? { ...r, app_role: val } : r));
    setDirty(true);
    setSuccess(false);
  }, []);

  const handleDelete = useCallback((key) => {
    setRows(prev => prev.filter(r => r._key !== key));
    setDirty(true);
    setSuccess(false);
  }, []);

  const handleAdd = useCallback(() => {
    setRows(prev => [...prev, { _key: Date.now(), okta_group: "", app_role: "viewer" }]);
    setDirty(true);
    setSuccess(false);
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);
    setSuccess(false);
    setSaving(true);
    try {
      const payload = rows.map(r => ({
        okta_group: r.okta_group.trim(),
        app_role:   r.app_role,
      }));
      const data = await putRoleMappings(payload);
      setRows((data.mappings ?? []).map((m, i) => ({
        _key:       i,
        okta_group: m.okta_group,
        app_role:   launchRole(m.app_role),
      })));
      setDirty(false);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ValidationError) {
        setError(err.message || "Validation error - check your mappings.");
      } else if (err instanceof AuthError) {
        setError("Permission denied.");
      } else {
        setError("Failed to save role mappings. Please try again.");
        log.error("RoleManagementPanel: putRoleMappings failed", { err: String(err) });
      }
    } finally {
      setSaving(false);
    }
  }, [rows]);

  return (
    <div data-testid="role-management-panel">
      <p style={TITLE}>Roles &amp; Permissions</p>
      {explainer}

      {error && <ErrorBanner message={error} />}

      <section data-testid="role-capabilities" style={{ marginBottom: 24 }}>
        <h3 style={{ ...TITLE, fontSize: T.fsMd5, marginBottom: 6 }}>Launch role capabilities</h3>
        <p style={{ ...SUBTITLE, fontSize: T.fsSm, marginBottom: 12 }}>
          These summaries describe the existing role gates. They do not create new permissions.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {ROLE_CAPABILITIES.map((capability) => {
            const selectedRole = new URLSearchParams(window.location.search).get("role");
            const selected = launchRole(selectedRole) === capability.role;
            return (
              <div key={capability.role} data-testid={`role-capability-${capability.role}`} style={{ border: `1px solid ${selected ? T.blue : T.border}`, borderRadius: 6, padding: 12, background: selected ? T.statusLiveBg : T.white }}>
                <strong style={{ color: T.dark }}>{capability.title}</strong>
                <div style={{ marginTop: 5, color: T.mid, fontSize: T.fsSm }}>{capability.summary}</div>
              </div>
            );
          })}
        </div>
      </section>

      {success && (
        <div style={{
          background: T.statusLiveBg, border: `1px solid ${T.greenBadge}`,
          borderRadius: 6, padding: "10px 14px", marginBottom: 14,
          fontSize: T.fsBase, color: T.confirmedGreen, fontWeight: T.fwSemibold,
        }}>
          Mappings saved successfully.
        </div>
      )}

      {/* Mapping table */}
      <div style={{ border: `0.5px solid ${T.border}`, borderRadius: 6, overflowX: "auto", marginBottom: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: "55%" }}>Okta group name</th>
              <th style={TH}>App role</th>
              <th style={{ ...TH, width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} style={{ ...TD, textAlign: "center", color: T.light, padding: "20px 12px" }}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ ...TD, textAlign: "center", color: T.light, padding: "20px 12px" }}>
                  No mappings - add one below
                </td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row._key}>
                <td style={TD}>
                  <input
                    data-testid={`mapping-group-${row._key}`}
                    type="text"
                    value={row.okta_group}
                    onChange={e => handleGroupChange(row._key, e.target.value)}
                    placeholder="e.g. replit-workplace-app-admin"
                    style={{
                      width: "100%",
                      padding: "5px 8px",
                      fontSize: T.fsSm5,
                      border: `1px solid ${T.border}`,
                      borderRadius: 4,
                      outline: "none",
                      color: T.dark,
                      fontFamily: "monospace",
                      boxSizing: "border-box",
                    }}
                  />
                </td>
                <td style={TD}>
                  <select
                    data-testid={`mapping-role-${row._key}`}
                    value={row.app_role}
                    onChange={e => handleRoleChange(row._key, e.target.value)}
                    style={{
                      padding: "5px 8px",
                      fontSize: T.fsSm5,
                      border: `1px solid ${T.border}`,
                      borderRadius: 4,
                      color: T.dark,
                      background: T.white,
                      cursor: "pointer",
                    }}
                  >
                    <option value="admin">Admin</option>
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td style={{ ...TD, textAlign: "center" }}>
                  <button
                    data-testid={`mapping-delete-${row._key}`}
                    onClick={() => handleDelete(row._key)}
                    title="Remove mapping"
                    style={{
                      background: "none", border: "none",
                      cursor: "pointer", color: T.light,
                      fontSize: T.fsLgX, lineHeight: 1, padding: "0 4px",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = T.red}
                    onMouseLeave={e => e.currentTarget.style.color = T.light}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <button
          data-testid="add-mapping-btn"
          onClick={handleAdd}
          style={{
            padding: "6px 12px",
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            borderRadius: 6,
            border: `1px solid ${T.border}`,
            background: T.white,
            color: T.dark,
            cursor: "pointer",
          }}
        >
          + Add mapping
        </button>

        <button
          data-testid="save-mappings-btn"
          onClick={handleSave}
          disabled={saving || !dirty}
          style={{
            padding: "8px 18px",
            fontSize: T.fsBase,
            fontWeight: T.fwSemibold,
            borderRadius: 6,
            border: `1px solid ${T.blueSoftBorder}`,
            background: dirty ? T.blue : T.lightGray,
            color: dirty ? T.white : T.light,
            cursor: saving || !dirty ? "not-allowed" : "pointer",
            opacity: saving ? 0.65 : 1,
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

    </div>
  );
}

const CLASSIFICATION_LABELS = {
  general: "General",
  financial_sensitive: "Financial / sensitive",
};

function areaLabel(areaKey) {
  return areaKey
    .split(".")
    .map((part) => part.replaceAll("_", " "))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" · ");
}

export function SensitivityClassificationEditor() {
  const [areas, setAreas] = useState({});
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const loadTaxonomy = useCallback(() => {
    setLoading(true);
    setError(null);
    return getSensitivityTaxonomy()
      .then((data) => {
        const taxonomy = data?.taxonomy;
        setAreas(taxonomy?.areas && typeof taxonomy.areas === "object" ? taxonomy.areas : {});
        setClassifications(
          Array.isArray(taxonomy?.classifications) ? taxonomy.classifications : [],
        );
        setDirty(false);
      })
      .catch((err) => {
        if (err instanceof AuthError) setError("Permission denied.");
        else {
          setError("Failed to load sensitivity classifications.");
          log.error("SensitivityClassificationEditor: load failed", { err: String(err) });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTaxonomy().catch(() => {});
  }, [loadTaxonomy]);

  const handleChange = useCallback((areaKey, classification) => {
    setAreas((prev) => ({ ...prev, [areaKey]: classification }));
    setDirty(true);
    setSuccess(false);
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await putSensitivityTaxonomy(areas);
      const taxonomy = data?.taxonomy;
      setAreas(taxonomy?.areas && typeof taxonomy.areas === "object" ? taxonomy.areas : {});
      setClassifications(
        Array.isArray(taxonomy?.classifications) ? taxonomy.classifications : classifications,
      );
      setDirty(false);
      setSuccess(true);
    } catch (err) {
      if (err instanceof ValidationError) setError(err.message || "Invalid classification.");
      else if (err instanceof AuthError) setError("Permission denied.");
      else {
        setError("Failed to save sensitivity classifications. Please try again.");
        log.error("SensitivityClassificationEditor: save failed", { err: String(err) });
      }
    } finally {
      setSaving(false);
    }
  }, [areas, classifications]);

  return (
    <section
      data-testid="sensitivity-classification-editor"
      style={{ borderTop: `1px solid ${T.border}`, paddingTop: 24 }}
    >
      <p style={{ ...TITLE, fontSize: T.fsLg }}>Sensitivity classifications</p>
      <p style={{ ...SUBTITLE, marginBottom: 16 }}>
        Choose which supported areas are visible to Workplace View users. Unknown
        or malformed classifications remain restricted by default. Changes apply
        when users start a new authenticated session.
      </p>

      {error && <ErrorBanner message={error} />}
      {success && (
        <div style={{
          background: T.statusLiveBg, border: `1px solid ${T.greenBadge}`,
          borderRadius: 6, padding: "10px 14px", marginBottom: 14,
          fontSize: T.fsBase, color: T.confirmedGreen, fontWeight: T.fwSemibold,
        }}>
          Sensitivity classifications saved successfully.
        </div>
      )}

      <div style={{ border: `0.5px solid ${T.border}`, borderRadius: 6, overflowX: "auto", marginBottom: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: "55%" }}>Supported area</th>
              <th style={TH}>Classification</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={2} style={{ ...TD, textAlign: "center", color: T.light, padding: "20px 12px" }}>
                  Loading…
                </td>
              </tr>
            ) : Object.keys(areas).length === 0 ? (
              <tr>
                <td colSpan={2} style={{ ...TD, textAlign: "center", color: T.light, padding: "20px 12px" }}>
                  No supported areas are configured.
                </td>
              </tr>
            ) : Object.entries(areas).map(([areaKey, classification]) => (
              <tr key={areaKey}>
                <td style={TD}>
                  <strong>{areaLabel(areaKey)}</strong>
                  <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 2 }}>{areaKey}</div>
                </td>
                <td style={TD}>
                  <select
                    data-testid={`sensitivity-classification-${areaKey}`}
                    value={classification}
                    onChange={(e) => handleChange(areaKey, e.target.value)}
                    style={{
                      padding: "5px 8px", fontSize: T.fsSm5,
                      border: `1px solid ${T.border}`, borderRadius: 4,
                      color: T.dark, background: T.white, cursor: "pointer",
                    }}
                  >
                    {classifications.map((value) => (
                      <option key={value} value={value}>
                        {CLASSIFICATION_LABELS[value] ?? value}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          data-testid="save-sensitivity-taxonomy-btn"
          onClick={handleSave}
          disabled={saving || loading || !dirty}
          style={{
            padding: "8px 18px", fontSize: T.fsBase, fontWeight: T.fwSemibold,
            borderRadius: 6, border: `1px solid ${T.blueSoftBorder}`,
            background: dirty ? T.blue : T.lightGray, color: dirty ? T.white : T.light,
            cursor: saving || loading || !dirty ? "not-allowed" : "pointer",
            opacity: saving ? 0.65 : 1,
          }}
        >
          {saving ? "Saving…" : "Save classifications"}
        </button>
      </div>
    </section>
  );
}
